import { token } from '@/infrastructure/container';
import type { IAccessGateway } from '@/domain/accessGateway';
import type { ProjectState } from '@/domain/project';
import type { IReportGateway } from '@/domain/reportGateway';
import type { SessionState } from '@/domain/session';
import type { ILogger } from './Logger';

// The reporting facade over the two gateways this app already has, pinned by
// session/contracts/p5-reporting.md. It owns no handle and no SDK edge of its own, which is the
// whole point: the session aggregate is opened by `SessionGateway` and the project fold is
// `ProjectGateway`'s, so a report reads exactly what the rest of the laptop reads.
//
// No redemption. The admin device CREATED the project and the sessions on it, and creating an
// aggregate is what confers ownership (docs/access.md, docs/sync.md), so there is no code to scan
// and nothing to redeem. A laptop that is not the one that ran the day fails on the grant, which is
// a ForbiddenError and a sentence on the screen.
//
// Opening a session here is the same open the run list does when the admin selects a session, on
// the same instance, which is what keeps "one aggregate, one fold" true (INVARIANTS rule 5's
// one-gateway-many-tokens shape). It also means the P2 detach semantics are inherited rather than
// rebuilt: `SessionGateway.open` unsubscribes and closes the previous session BEFORE it attaches
// the next one, so exporting session B after session A cannot leave A's subscription writing A's
// fold over B's. Rebuilding that here with a second handle would be a second copy of the same
// mistake, and the SDK hands out one handle per aggregate id anyway.

// What a report needs from the session side: the fold, after somebody has opened it. Deliberately
// narrower than `ICoachGateway` and `ITickGateway`, which both carry `execute`: reporting never
// writes, and an interface that cannot write is a report that cannot corrupt a finished session.
export interface ISessionFold {
  readonly sessionState: SessionState; // MobX observable, the current fold
}

export const TOKEN_ISessionFold = token<ISessionFold>('ISessionFold');

export class ReportGateway implements IReportGateway {
  constructor(
    private readonly access: IAccessGateway,
    private readonly session: ISessionFold,
    private readonly logger: ILogger,
  ) {}

  // The day's run list, live off the project fold. Not copied into this object: a snapshot taken at
  // construction would freeze the picker at whatever the laptop had caught up to at boot.
  get projectState(): ProjectState {
    return this.access.projectState;
  }

  // Opens the session aggregate and hands back the folded state. `openSession` awaits the first
  // catch-up and translates whatever the SDK threw into the taxonomy, so a caller above here only
  // ever sees a DomainError (INVARIANTS rule 17).
  //
  // A session that was scheduled and never started has no aggregate, which reads 404 and folds to
  // EMPTY rather than throwing, so its report is the header line and nothing else. That is the
  // honest answer: nobody registered, so nobody is in the file.
  loadSession = async (sessionId: string): Promise<SessionState> => {
    this.logger.debug(`report: opening session ${sessionId}`);
    await this.access.openSession(sessionId);
    return this.session.sessionState;
  };
}

export const TOKEN_ReportGateway = token<ReportGateway>('ReportGateway');
